import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { NiveauLivreurService, StatsLivreurDTO, NiveauLivreurDTO } from 'src/app/core/services/niveau-livreur.service';
import { PersonnelRhService, PrimeMensuelleDTO, PersonnelRHDTO, ReleveLivreurDTO } from 'src/app/core/services/personnel-rh.service';

@Component({
  selector: 'app-livreur-fiches-paie',
  templateUrl: './livreur-fiches-paie.component.html',
  styleUrls: ['./livreur-fiches-paie.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LivreurFichesPaieComponent implements OnInit {

  stats: StatsLivreurDTO | null = null;
  niveaux: NiveauLivreurDTO[] = [];
  primes: PrimeMensuelleDTO[] = [];
  personnel: PersonnelRHDTO | null = null;

  primeSelectionnee: PrimeMensuelleDTO | null = null;
  releveSelectionne: ReleveLivreurDTO | null = null;
  chargementReleve = false;

  chargement = true;
  erreur = '';
  userId: number | null = null;
  nomLivreur = '';

  readonly MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
                      'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  constructor(
    private auth: AuthService,
    private niveauService: NiveauLivreurService,
    private rhService: PersonnelRhService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const u = this.auth.obtenirUtilisateurCourant();
    if (!u?.userid) {
      this.erreur = 'Session invalide. Reconnectez-vous.';
      this.chargement = false;
      this.cdr.markForCheck();
      return;
    }
    this.userId = u.userid;
    this.nomLivreur = `${u.prenom || ''} ${u.nom || ''}`.trim() || u.username || '';

    forkJoin({
      stats: this.niveauService.getStats(u.userid).pipe(catchError(() => of(null))),
      niveaux: this.niveauService.lister().pipe(catchError(() => of([]))),
      personnel: this.rhService.getPersonnelByUserId(u.userid).pipe(catchError(() => of(null))),
      primes: this.rhService.getHistoriquePrimesParUser(u.userid).pipe(catchError(() => of([])))
    }).subscribe(({ stats, niveaux, personnel, primes }) => {
      this.stats = stats as StatsLivreurDTO | null;
      this.niveaux = (niveaux as NiveauLivreurDTO[]).sort((a, b) => a.ordre - b.ordre);
      this.personnel = personnel as PersonnelRHDTO | null;
      this.primes = (primes as PrimeMensuelleDTO[]).sort((a, b) => b.moisAnnee.localeCompare(a.moisAnnee));
      this.chargement = false;
      this.cdr.markForCheck();
    });
  }

  // ── Niveaux ────────────────────────────────────────────────────────

  get coursesTotal(): number {
    return this.stats?.coursesLivrees ?? 0;
  }

  niveauAtteint(n: NiveauLivreurDTO): boolean {
    return this.coursesTotal >= n.seuilMinCourses;
  }

  get niveauActuel(): NiveauLivreurDTO | null {
    const atteints = this.niveaux.filter(n => this.niveauAtteint(n));
    return atteints.length ? atteints[atteints.length - 1] : null;
  }

  get niveauSuivant(): NiveauLivreurDTO | null {
    return this.niveaux.find(n => !this.niveauAtteint(n)) ?? null;
  }

  progressionVersNiveauSuivant(): number {
    const actuel = this.niveauActuel;
    const suivant = this.niveauSuivant;
    if (!suivant) return 100;
    const base = actuel?.seuilMinCourses ?? 0;
    const pct = ((this.coursesTotal - base) / (suivant.seuilMinCourses - base)) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  // ── Gains ──────────────────────────────────────────────────────────

  get totalPayeNet(): number {
    return this.primes
      .filter(p => p.statut === 'PAYEE')
      .reduce((s, p) => s + (p.montantBase || 0) + (p.montantPrime || 0), 0);
  }

  get totalPayeBase(): number {
    return this.primes.filter(p => p.statut === 'PAYEE').reduce((s, p) => s + (p.montantBase || 0), 0);
  }

  get totalPayePrime(): number {
    return this.primes.filter(p => p.statut === 'PAYEE').reduce((s, p) => s + (p.montantPrime || 0), 0);
  }

  totalMois(p: PrimeMensuelleDTO): number {
    return (p.montantBase || 0) + (p.montantPrime || 0);
  }

  // ── Relevé mensuel ─────────────────────────────────────────────────

  ouvrirReleve(p: PrimeMensuelleDTO): void {
    if (this.primeSelectionnee?.id === p.id) {
      this.primeSelectionnee = null;
      this.releveSelectionne = null;
      this.cdr.markForCheck();
      return;
    }
    this.primeSelectionnee = p;
    this.releveSelectionne = null;
    if (!this.personnel?.id) return;
    this.chargementReleve = true;
    this.cdr.markForCheck();
    this.rhService.getReleve(this.personnel.id, p.moisAnnee).subscribe({
      next: r => { this.releveSelectionne = r; this.chargementReleve = false; this.cdr.markForCheck(); },
      error: () => { this.chargementReleve = false; this.cdr.markForCheck(); }
    });
  }

  imprimer(): void { window.print(); }

  // ── Formatage ──────────────────────────────────────────────────────

  formaterMois(m: string): string {
    const [annee, mois] = m.split('-');
    return `${this.MOIS_FR[parseInt(mois, 10) - 1]} ${annee}`;
  }

  formaterMontant(v: number): string {
    return new Intl.NumberFormat('fr-FR').format(v) + ' FCFA';
  }

  statutClass(s: string): string {
    switch (s) {
      case 'PAYEE': return 'badge-payee';
      case 'VALIDEE': return 'badge-validee';
      case 'REJETEE': return 'badge-rejetee';
      default: return 'badge-attente';
    }
  }

  statutLabel(s: string): string {
    switch (s) {
      case 'PAYEE': return 'Payée';
      case 'VALIDEE': return 'Validée';
      case 'REJETEE': return 'Rejetée';
      default: return 'En attente';
    }
  }
}
